import { useInView } from "@/hooks/useAnimations";
import { Calculator, ArrowRight, ClipboardCheck, Search } from "lucide-react";
import { Link } from "react-router-dom";

export default function RechnerTeaser() {
  const [ref, isInView] = useInView();

  return (
    <section
      ref={ref}
      data-testid="rechner-teaser"
      className="py-14 lg:py-18 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div className={`text-center mb-8 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-['Manrope'] text-2xl sm:text-3xl font-bold text-slate-900 mb-2" id="pflege-helfer">
            Kostenlose digitale Pflege-Helfer
          </h2>
          <p className="text-base text-slate-500 max-w-2xl mx-auto">
            Unsere interaktiven Tools helfen Ihnen, Ihre Leistungen zu kennen und Anträge richtig vorzubereiten.
          </p>
        </div>

        <div className={`space-y-4 transition-all duration-700 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Pflegeleistungs-Finder — PRIMARY, first position */}
          <Link
            to="/blog/pflegeleistungen-finder"
            className="block bg-gradient-to-br from-cyan-50 to-white border-2 border-cyan-200 rounded-2xl p-6 sm:p-8 hover:shadow-xl hover:-translate-y-1 transition-all group"
            data-testid="pl-teaser-link"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="h-14 w-14 bg-cyan-100 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <Search className="h-7 w-7 text-cyan-700" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-white bg-cyan-700 px-2.5 py-1 rounded-full">Empfohlen</span>
                  <span className="text-xs font-semibold text-cyan-700 bg-cyan-50 px-2.5 py-1 rounded-full">Leistungen, Hilfsmittel & Zuschüsse</span>
                </div>
                <h3 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-1 group-hover:text-cyan-700 transition-colors">
                  Pflegeleistungs-Finder
                </h3>
                <p className="text-base text-slate-500 leading-relaxed">
                  Welche Pflegeleistungen, Hilfsmittel und Zuschüsse stehen Ihnen zu? Finden Sie es in wenigen Minuten heraus.
                </p>
              </div>
              <div className="shrink-0 hidden sm:flex items-center gap-2 bg-cyan-700 text-white rounded-xl px-5 py-3 font-semibold group-hover:bg-cyan-600 transition-all">
                Jetzt prüfen <ArrowRight className="h-5 w-5" />
              </div>
            </div>
          </Link>

          {/* Sozialraumbudget-Rechner */}
          <Link
            to="/blog/sozialraumbudget-rechner"
            className="block bg-white border border-cyan-200/60 rounded-2xl p-6 sm:p-8 hover:shadow-lg hover:-translate-y-1 transition-all group"
            data-testid="rechner-teaser-link"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="h-14 w-14 bg-cyan-50 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <Calculator className="h-7 w-7 text-cyan-700" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-white bg-cyan-700 px-2.5 py-1 rounded-full">Neu</span>
                  <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">Unverbindlich</span>
                </div>
                <h3 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-1 group-hover:text-cyan-700 transition-colors">
                  Sozialraumbudget-Rechner
                </h3>
                <p className="text-base text-slate-500 leading-relaxed">
                  Prüfen Sie unverbindlich, welche Auswirkungen aktuell diskutierte Pflegereformen künftig auf Ihre Situation haben könnten.
                </p>
              </div>
              <div className="shrink-0 hidden sm:flex items-center gap-2 text-cyan-700 font-semibold group-hover:gap-3 transition-all">
                Zum Rechner <ArrowRight className="h-5 w-5" />
              </div>
            </div>
          </Link>

          {/* Verhinderungspflege-Assistent */}
          <Link
            to="/blog/verhinderungspflege-antrag"
            className="block bg-white border border-green-200/60 rounded-2xl p-6 sm:p-8 hover:shadow-lg hover:-translate-y-1 transition-all group"
            data-testid="vp-teaser-link"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <div className="h-14 w-14 bg-green-50 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <ClipboardCheck className="h-7 w-7 text-green-700" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-white bg-green-700 px-2.5 py-1 rounded-full">Neu</span>
                  <span className="text-xs font-semibold text-green-700 bg-green-50 px-2.5 py-1 rounded-full">Schritt für Schritt</span>
                </div>
                <h3 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-1 group-hover:text-green-700 transition-colors">
                  Verhinderungspflege-Antragsassistent
                </h3>
                <p className="text-base text-slate-500 leading-relaxed">
                  Antrag auf Verhinderungspflege richtig ausfüllen — mit individueller Ausfüllhilfe, Checkliste und Tipps.
                </p>
              </div>
              <div className="shrink-0 hidden sm:flex items-center gap-2 text-green-700 font-semibold group-hover:gap-3 transition-all">
                Zum Assistenten <ArrowRight className="h-5 w-5" />
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
